import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock, Leaf } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/context/AdminAuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { token, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (token) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-5 grain" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8 text-white">
          <Leaf className="w-6 h-6 text-leaf" />
          <span className="font-heading text-xl font-semibold tracking-tight">CJB Admin</span>
        </div>
        <form onSubmit={submit} className="bg-white rounded-3xl p-8 shadow-2xl" data-testid="admin-login-form">
          <div className="w-12 h-12 rounded-2xl bg-leaf/10 flex items-center justify-center mb-5"><Lock className="w-5 h-5 text-leaf" /></div>
          <h1 className="font-heading text-2xl font-semibold text-navy mb-1">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to view bookings and leads.</p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-navy/70 text-xs uppercase tracking-widest">Email</Label>
              <Input id="email" type="email" data-testid="admin-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white focus-visible:ring-leaf" placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-navy/70 text-xs uppercase tracking-widest">Password</Label>
              <Input id="password" type="password" data-testid="admin-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} className="h-12 bg-white focus-visible:ring-leaf" placeholder="••••••••" />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 mt-4" data-testid="admin-login-error">{error}</p>}

          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full mt-6 py-3.5 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
